import React, { useEffect, useState } from 'react';
import {
    Database,
    Video,
    Bell,
    ShieldAlert,
    Network,
    AlertCircle,
    AlertTriangle,
    RefreshCw,
    CheckCircle2,
    HardDrive,
    Activity,
    Cpu,
    Globe,
    Fingerprint,
    Code,
    History,
    Repeat,
    HeartPulse,
    Settings,
    Server,
    ChevronRight,
} from 'lucide-react';
import baseapi from '../../services/baseapi';
import verbaseapi from '../../services/versionapi';
import { showError } from '../../utils/toast';


interface Partition {
    path: string;
    type: string;
    free_TB: number;
    used_TB: number;
    total_TB: number;
    is_error: boolean;
}

interface Disk {
    disk_name: string;
    state: string;
    total_TB: number;
    used_TB: number;
    free_TB: number;
    used_percent: number;
    has_errors: boolean;
    health_ok: boolean;
    status: string;
    partitions?: Partition[];
}

interface Channel {
    channel_number: number;
    name: string;
    is_named: boolean;
    recording_scheduled: boolean;
    motion_detect_enabled: boolean;
    tamper_detect_enabled: boolean;
    video_loss_enabled: boolean;
    alarm_input_enabled: boolean;
    alarm_sensor_type: string;
}

interface NVRData {
    client_id: number;
    nvr_address: string;
    report_time: string;
    device: {
        serial_number: string;
        model: string;
        device_type: string;
        processor: string;
        firmware: string;
        firmware_build: string;
        hardware: string;
        current_time: string;
    };
    network: {
        hostname: string;
        domain: string;
        interfaces: Array<{
            name: string;
            ip_address: string;
            mac_address: string;
            dhcp_enabled: boolean;
        }>;
        dns_primary: string;
        ntp: {
            enabled: boolean;
            server: string;
            timezone: string;
        };
    };
    storage: {
        summary: {
            total_disks: number;
            total_space_TB: number;
            used_space_TB: number;
            free_space_TB: number;
            overall_status: string;
            overwrite_enabled: boolean;
            min_retention_days: number;
        };
        disks: Disk[];
        alerts: any;
    };
    recording: {
        channels_recording: number[];
        channels_not_recording: number[];
        total_recording: number;
        total_not_recording: number;
    };
    channels: Channel[];
    events: any;
    analysis: {
        total_critical: number;
        total_warnings: number;
        total_ok: number;
        critical_issues: string[];
        warnings: string[];
        ok: string[];
    };
}

const mapResponseToNVRData = (device: any): NVRData => {
    let rawDisks = [];
    if (typeof device.disks === 'string') {
        try {
            rawDisks = JSON.parse(device.disks);
        } catch (e) {
            console.error('Failed to parse disks:', e);
        }
    } else if (Array.isArray(device.disks)) {
        rawDisks = device.disks;
    }

    const parseNum = (val: any): number => {
        if (typeof val === 'number') return val;
        if (typeof val === 'string') {
            const parsed = parseFloat(val);
            return isNaN(parsed) ? 0 : parsed;
        }
        return 0;
    };

    const disks: Disk[] = rawDisks.map((d: any) => ({
        disk_name: d.disk_name || '',
        state: d.state || '',
        total_TB: parseNum(d.total_TB ?? d.total_tb ?? 0),
        used_TB: parseNum(d.used_TB ?? d.used_tb ?? 0),
        free_TB: parseNum(d.free_TB ?? d.free_tb ?? 0),
        used_percent: parseNum(d.used_percent ?? d.used_percent ?? 0),
        has_errors: d.has_errors === true || d.has_errors === 'true',
        health_ok: d.health_ok === true || d.health_ok === 'true' || d.health_ok === undefined,
        status: d.status || '',
        partitions: (d.partitions || []).map((p: any) => ({
            path: p.path || '',
            type: p.type || '',
            free_TB: parseNum(p.free_TB ?? p.free_tb ?? 0),
            used_TB: parseNum(p.used_TB ?? p.used_tb ?? 0),
            total_TB: parseNum(p.total_TB ?? p.total_tb ?? 0),
            is_error: p.is_error === true || p.is_error === 'true',
        })),
    }));

    let channels: Channel[] = [];
    if (typeof device.channels === 'string') {
        try {
            channels = JSON.parse(device.channels);
        } catch (e) {
            console.error('Failed to parse channels:', e);
        }
    } else if (Array.isArray(device.channels)) {
        channels = device.channels;
    }

    const storageSummary = {
        total_disks: parseNum(device.storage?.total_disks ?? device.total_disks ?? 0),
        total_space_TB: parseNum(device.storage?.total_space_TB ?? device.storage?.total_space_tb ?? device.total_space_tb ?? device.total_space_TB ?? 0),
        used_space_TB: parseNum(device.storage?.used_space_TB ?? device.storage?.used_space_tb ?? device.used_space_tb ?? device.used_space_TB ?? 0),
        free_space_TB: parseNum(device.storage?.free_space_TB ?? device.storage?.free_space_tb ?? device.free_space_tb ?? device.free_space_TB ?? 0),
        overall_status: device.storage?.overall_status ?? device.overall_status ?? '',
        overwrite_enabled: !!(device.storage?.overwrite_enabled ?? device.overwrite_enabled),
        min_retention_days: parseNum(device.storage?.min_retention_days ?? device.min_retention_days ?? 0),
    };

    return {
        client_id: device.client_id,
        nvr_address: device.nvr_address,
        report_time: device.report_time,
        device: {
            serial_number: device.serial_number || '',
            model: device.model || '',
            device_type: device.device_type || '',
            processor: device.processor || '',
            firmware: device.firmware || '',
            firmware_build: device.firmware_build || '',
            hardware: device.hardware || '',
            current_time: device.current_time || '',
        },
        network: {
            hostname: device.network?.hostname || '',
            domain: device.network?.domain || '',
            interfaces: (device.network?.interfaces || []).map((inf: any) => ({
                name: inf.name || '',
                ip_address: inf.ip_address || '',
                mac_address: inf.mac_address || '',
                dhcp_enabled: !!inf.dhcp_enabled,
            })),
            dns_primary: device.network?.dns_primary || '',
            ntp: {
                enabled: !!device.network?.ntp?.enabled,
                server: device.network?.ntp?.server || '',
                timezone: device.network?.ntp?.timezone || '',
            },
        },
        storage: {
            summary: storageSummary,
            disks: disks,
            alerts: device.storage_alerts || null,
        },
        recording: {
            channels_recording: device.recording?.channels_recording || [],
            channels_not_recording: device.recording?.channels_not_recording || [],
            total_recording: parseNum(device.recording?.total_recording ?? device.total_recording ?? 0),
            total_not_recording: parseNum(device.recording?.total_not_recording ?? device.total_not_recording ?? 0),
        },
        channels: channels,
        events: device.events || null,
        analysis: {
            total_critical: parseNum(device.analysis?.total_critical ?? device.total_critical ?? 0),
            total_warnings: parseNum(device.analysis?.total_warnings ?? device.total_warnings ?? 0),
            total_ok: parseNum(device.analysis?.total_ok ?? device.total_ok ?? 0),
            critical_issues: device.analysis?.critical_issues || [],
            warnings: device.analysis?.warnings || [],
            ok: device.analysis?.ok || [],
        },
    };
};

const NVRDashboard: React.FC<{ clientId: number }> = ({ clientId }) => {
    const [loading, setLoading] = useState(true);
    const [data, setData] = useState<NVRData | null>(null);
    const [error, setError] = useState<string | null>(null);
    const [isAccessing, setIsAccessing] = useState(false);
    const [expandedDisks, setExpandedDisks] = useState<Record<number, boolean>>({});

    const toggleDisk = (idx: number) => {
        setExpandedDisks(prev => ({
            ...prev,
            [idx]: !prev[idx]
        }));
    };

    const handleAccessNVR = async () => {
        setIsAccessing(true);
        try {
            const response = await verbaseapi.post(`/nvr-proxy/${clientId}/select`);
            if (response.data && response.data.nvr_url) {
                const nvrUrl = response.data.nvr_url;
                const targetUrl = nvrUrl.startsWith('http')
                    ? nvrUrl
                    : `${window.location.origin}${nvrUrl.startsWith('/') ? '' : '/'}${nvrUrl}`;
                window.open(targetUrl, '_blank');
            } else {
                showError('Failed to retrieve NVR Access URL.');
            }
        } catch (err: any) {
            console.error('Error accessing NVR:', err);
            showError(err.response?.data?.message || 'Error initiating NVR remote access.');
        } finally {
            setIsAccessing(false);
        }
    };


    const fetchData = async () => {
        setLoading(true);
        try {
            const response = await baseapi.get('/nvr_audit/nvr_data', { params: { client_id: clientId } });
            if (response.data.status && response.data.data.devices.length > 0) {
                setData(mapResponseToNVRData(response.data.data.devices[0]));
            } else {
                setError('No NVR data found for this client.');
            }
        } catch (err) {
            console.error('Error fetching NVR data:', err);
            setError('Failed to load NVR data.');
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchData();
    }, [clientId]);

    if (loading) {
        return (
            <div className="flex flex-col items-center justify-center p-20 space-y-4">
                <RefreshCw className="w-10 h-10 text-[#422568] animate-spin" />
                <p className="text-[#422568] font-bold animate-pulse">Analyzing NVR Diagnostics...</p>
            </div>
        );
    }

    if (error || !data) {
        return (
            <div className="flex flex-col items-center justify-center p-20 space-y-4">
                <AlertCircle className="w-12 h-12 text-slate-300" />
                <p className="text-slate-500 font-medium">{error || 'Data unavailable'}</p>
                <button
                    onClick={fetchData}
                    className="px-4 py-2 bg-[#422568] text-white rounded-lg hover:bg-[#422568] transition-colors shadow-sm font-bold"
                >
                    Retry
                </button>
            </div>
        );
    }

    return (
        <div className="p-4 bg-[#f8fafc] min-h-full space-y-4 animate-in fade-in duration-500 text-slate-800 pb-20">
            {/* Top Summary Cards */}
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
                <NVRStatsCard
                    label="Storage"
                    value={`${data.storage.summary.used_space_TB}/${data.storage.summary.total_space_TB} TB`}
                    subtitle="Used Space"
                    icon={<Database className="w-4 h-4 text-white" />}
                    color="orange"
                />
                <NVRStatsCard
                    label="Disks"
                    value={`${data.storage.summary.total_disks} Disks`}
                    subtitle={data.storage.summary.overall_status}
                    icon={<HardDrive className="w-4 h-4" />}
                    color="red"
                />
                <NVRStatsCard
                    label="Motion"
                    value={`${data.events.motion_detection?.total_enabled || 0}/${data.channels.length}`}
                    subtitle="Detection Enabled"
                    icon={<Activity className="w-4 h-4" />}
                    color="blue"
                />
                <div className="bg-white p-3 rounded-xl border border-slate-100 shadow-sm flex items-center justify-between gap-3 transition-all hover:shadow-md cursor-default">
                    <div className="flex items-center gap-3 min-w-0 flex-1">
                        <div className="p-2.5 rounded-lg bg-[#422568]/10 text-[#422568] flex-shrink-0">
                            {isAccessing ? (
                                <RefreshCw className="w-4 h-4 animate-spin" />
                            ) : (
                                <Globe className="w-4 h-4" />
                            )}
                        </div>
                        <div className="min-w-0 flex-1">
                            <p className="text-[9px] font-black uppercase tracking-wider leading-none mb-1 opacity-70 text-[#422568]">
                                Remote Access
                            </p>
                            <p className="text-sm font-extrabold text-slate-800 leading-none truncate">
                                Access NVR
                            </p>
                            <p className="text-[9px] font-bold text-slate-800 mt-1 truncate">
                                Open in new tab
                            </p>
                        </div>
                    </div>
                    <button
                        onClick={handleAccessNVR}
                        disabled={isAccessing}
                        className="flex-shrink-0 px-3 py-1.5 bg-[#422568] hover:bg-[#52337d] active:scale-95 disabled:opacity-50 text-white rounded-md transition-all shadow-sm font-extrabold text-[10px] uppercase tracking-wider flex items-center justify-center gap-1 cursor-pointer"
                    >
                        {isAccessing ? (
                            <>
                                <RefreshCw className="w-2.5 h-2.5 animate-spin" />
                                <span>Connecting</span>
                            </>
                        ) : (
                            <span>Access</span>
                        )}
                    </button>
                </div>

            </div>

            <div className="grid grid-cols-1 xl:grid-cols-12 gap-4">
                {/* Row 2: Device & Network (Full Width) */}
                <div className="xl:col-span-12">
                    <CompactCard title="Device & Network" icon={<Network className="w-4 h-4" />} color="indigo">
                        <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-3 py-2">
                            <DeviceDetailItem label="Model" value={data.device.model} icon={<Settings className="w-3.5 h-3.5" />} iconColor="text-blue-500" />
                            <DeviceDetailItem label="Processor" value={data.device.processor} icon={<Cpu className="w-3.5 h-3.5" />} iconColor="text-purple-500" />
                            <DeviceDetailItem label="Firmware" value={data.device.firmware} icon={<Code className="w-3.5 h-3.5" />} iconColor="text-teal-500" />
                            <DeviceDetailItem label="NTP Sync" value={data.network.ntp.enabled ? 'Enabled' : 'Disabled'} warning={!data.network.ntp.enabled} success={data.network.ntp.enabled} icon={<RefreshCw className="w-3.5 h-3.5" />} iconColor="text-emerald-500" />
                            <DeviceDetailItem label="IP Address" value={data.network.interfaces[0]?.ip_address} icon={<Globe className="w-3.5 h-3.5" />} iconColor="text-indigo-500" />
                            <DeviceDetailItem label="MAC Address" value={data.network.interfaces[0]?.mac_address} icon={<Fingerprint className="w-3.5 h-3.5" />} iconColor="text-slate-500" />
                        </div>
                    </CompactCard>
                </div>

                {/* Row 3: Equal Height Columns */}
                {/* Column 1: Storage Details */}
                <div className="xl:col-span-4">
                    <CompactCard title="Storage Details" icon={<Database className="w-4 h-4 text-[#422568]" />} color="orange" className="h-[360px]">
                        {/* Table Header */}
                        <div className="flex items-center justify-between py-1.5 px-1 border-b border-slate-100 text-[8px] font-black text-slate-800 uppercase tracking-wider bg-slate-50/50 flex-shrink-0">
                            <span className="w-[25%]">Disk</span>
                            <span className="w-[30%]">Capacity</span>
                            <span className="w-[30%]">Usage</span>
                            <span className="w-[15%] text-right">Status</span>
                        </div>

                        {/* Compact Tabular Disks List */}
                        <div className="flex-1 overflow-y-auto pr-0.5 custom-scroll space-y-1">
                            {data.storage.disks.map((disk, idx) => {
                                const diskName = disk.disk_name || '';
                                const isHealthy = disk.health_ok && !disk.has_errors;
                                const shortName = diskName.split('/').pop() || diskName;
                                const isExpanded = !!expandedDisks[idx];

                                return (
                                    <div key={idx} className="border-b border-slate-100 last:border-0 py-1">
                                        {/* Disk Row */}
                                        <div
                                            onClick={() => toggleDisk(idx)}
                                            className="flex items-center justify-between text-[10px] hover:bg-slate-50/80 px-1 py-1 rounded transition-colors cursor-pointer select-none"
                                        >
                                            {/* Disk Name & Health Icon */}
                                            <div className="flex items-center gap-1.5 w-[25%] min-w-0">
                                                <Server className={`w-3 h-3 flex-shrink-0 ${isHealthy ? 'text-[#422568]' : 'text-red-500 animate-pulse'}`} />
                                                <span className="font-extrabold text-slate-700 truncate" title={diskName}>
                                                    {shortName}
                                                </span>
                                                {disk.partitions && disk.partitions.length > 0 && (
                                                    <span className="text-[7px] bg-[#422568]/10 text-[#422568] font-black px-1.5 py-0.2 rounded-md">
                                                        {disk.partitions.length}
                                                    </span>
                                                )}
                                            </div>

                                            {/* Capacity Used/Total */}
                                            <div className="w-[30%] text-slate-500 font-bold text-left text-[9px] truncate">
                                                {(disk.used_TB || 0).toFixed(2)}/{(disk.total_TB || 0).toFixed(2)} TB
                                            </div>

                                            {/* Progress Bar & Percentage */}
                                            <div className="w-[30%] flex items-center gap-1.5">
                                                <div className="h-1 flex-1 bg-slate-100 rounded-full overflow-hidden border border-slate-200/50">
                                                    <div
                                                        className={`h-full rounded-full transition-all duration-300 ${!isHealthy
                                                            ? 'bg-red-500'
                                                            : (disk.used_percent || 0) >= 90
                                                                ? 'bg-amber-500'
                                                                : 'bg-emerald-500'
                                                            }`}
                                                        style={{ width: `${disk.used_percent || 0}%` }}
                                                    ></div>
                                                </div>
                                                <span className={`font-black text-[9px] text-right w-7 ${(disk.used_percent || 0) >= 90 ? 'text-amber-600' : 'text-slate-600'}`}>
                                                    {disk.used_percent || 0}%
                                                </span>
                                            </div>

                                            {/* Status Badge */}
                                            <div className="w-[15%] flex justify-end items-center gap-1.5">
                                                <span className={`px-1 py-0.2 rounded text-[8px] font-black uppercase tracking-wider ${!isHealthy
                                                    ? 'bg-red-50 text-red-600 border border-red-100'
                                                    : disk.status === 'FULL'
                                                        ? 'bg-amber-50 text-amber-600 border border-amber-100'
                                                        : 'bg-emerald-50 text-emerald-600 border border-emerald-100'
                                                    }`}>
                                                    {!isHealthy ? 'FAIL' : disk.status || 'OK'}
                                                </span>
                                                {disk.partitions && disk.partitions.length > 0 && (
                                                    <ChevronRight className={`w-3 h-3 text-slate-800 transition-transform duration-200 ${isExpanded ? 'rotate-90 text-[#422568]' : ''}`} />
                                                )}
                                            </div>
                                        </div>

                                        {/* Partitions Details */}
                                        {isExpanded && disk.partitions && disk.partitions.length > 0 && (
                                            <div className="bg-slate-50/50 rounded-lg p-2 mt-1 mb-1 border border-slate-100/60 space-y-1.5 mx-1">
                                                <div className="flex justify-between items-center text-[7.5px] font-black text-slate-800 uppercase tracking-wider px-1">
                                                    <span className="w-[35%]">Partition Path</span>
                                                    <span className="w-[15%] text-center">Type</span>
                                                    <span className="w-[30%]">Capacity</span>
                                                    <span className="w-[20%] text-right">Status</span>
                                                </div>
                                                {disk.partitions.map((part, pIdx) => {
                                                    const isPartError = !!part.is_error;
                                                    const partName = part.path.split('/').pop() || part.path;
                                                    return (
                                                        <div key={pIdx} className="flex justify-between items-center text-[8px] text-slate-600 px-1 border-t border-slate-100/50 pt-1">
                                                            <span className="w-[35%] font-black text-slate-700 truncate" title={part.path}>
                                                                {partName}
                                                            </span>
                                                            <span className="w-[15%] text-center text-slate-500 font-bold">{part.type}</span>
                                                            <span className="w-[30%] text-slate-550 font-bold">
                                                                {(part.used_TB || 0).toFixed(2)}/{(part.total_TB || 0).toFixed(2)} TB
                                                            </span>
                                                            <span className="w-[20%] text-right">
                                                                <span className={`px-1 py-0.2 rounded-[3px] text-[7px] font-black uppercase ${isPartError
                                                                    ? 'bg-rose-50 text-rose-600 border border-rose-100'
                                                                    : 'bg-emerald-50 text-emerald-600 border border-emerald-100'
                                                                    }`}>
                                                                    {isPartError ? 'ERROR' : 'OK'}
                                                                </span>
                                                            </span>
                                                        </div>
                                                    );
                                                })}
                                            </div>
                                        )}
                                    </div>
                                );
                            })}
                        </div>

                        {/* Storage summary info */}
                        <div className="pt-2 grid grid-cols-2 gap-x-4 gap-y-1 border-t border-[#422568]/20 mt-2 flex-shrink-0">
                            <InfoRow label="Overwrite" value={data.storage.summary.overwrite_enabled ? 'YES' : 'NO'} highlight icon={<Repeat className="w-3 h-3" />} iconColor="text-[#422568]" labelColor="text-[#422568]/60" />
                            <InfoRow label="Retention" value={`${data.storage.summary.min_retention_days}d`} icon={<History className="w-3 h-3" />} iconColor="text-blue-500" labelColor="text-blue-900/60" />
                            <InfoRow label="Errors" value={data.storage.disks.some(d => d.has_errors) ? 'YES' : 'NO'} error={data.storage.disks.some(d => d.has_errors)} icon={<ShieldAlert className="w-3 h-3" />} iconColor="text-red-500" labelColor="text-red-900/60" />
                            <InfoRow label="Health" value={data.storage.disks.every(d => d.health_ok) ? 'OK' : 'FAIL'} success={data.storage.disks.every(d => d.health_ok)} icon={<HeartPulse className="w-3 h-3" />} iconColor="text-emerald-500" labelColor="text-emerald-900/60" />
                        </div>
                    </CompactCard>
                </div>

                {/* Column 2: Channels Grid */}
                <div className="xl:col-span-4">
                    <CompactCard title={`Channels (${data.channels.length})`} icon={<Video className="w-4 h-4" />} color="amber" className="h-[360px]">
                        <div className="flex-1 overflow-y-auto pr-0.5 custom-scroll">
                            <div className="grid grid-cols-4 gap-2">
                                {data.channels.map((ch) => (
                                    <div
                                        key={ch.channel_number}
                                        className={`p-2 rounded-lg border text-center transition-all ${ch.is_named ? 'border-amber-200 bg-amber-50/40 shadow-sm' : 'border-slate-100 bg-slate-50/30'}`}
                                    >
                                        <span className={`text-[9px] font-extrabold block leading-none ${ch.is_named ? 'text-amber-500' : 'text-slate-800'}`}>CH{String(ch.channel_number).padStart(2, '0')}</span>
                                        <span className={`text-[10px] font-black block truncate my-1 ${ch.is_named ? 'text-amber-900' : 'text-slate-600'}`}>{ch.name || 'IPC'}</span>
                                        <div className="flex justify-center gap-0.5">
                                            <StatusDot active={ch.recording_scheduled} color="bg-emerald-500" />
                                            <StatusDot active={ch.motion_detect_enabled} color="bg-blue-500" />
                                            <StatusDot active={ch.tamper_detect_enabled} color="bg-amber-500" />
                                            <StatusDot active={ch.alarm_input_enabled} color="bg-red-500" />
                                        </div>
                                    </div>
                                ))}
                            </div>
                        </div>
                        <div className="mt-3 flex items-center gap-3 text-[9px] font-bold border-t border-amber-50 pt-2 justify-center flex-shrink-0">
                            <span className="flex items-center gap-1 text-emerald-600"><div className="w-1.5 h-1.5 rounded-full bg-emerald-500"></div> rec</span>
                            <span className="flex items-center gap-1 text-blue-600"><div className="w-1.5 h-1.5 rounded-full bg-blue-500"></div> mot</span>
                            <span className="flex items-center gap-1 text-amber-600"><div className="w-1.5 h-1.5 rounded-full bg-amber-500"></div> tmp</span>
                            <span className="flex items-center gap-1 text-red-600"><div className="w-1.5 h-1.5 rounded-full bg-red-500"></div> alr</span>
                        </div>
                    </CompactCard>
                </div>

                {/* Column 3: Analysis & Events */}
                <div className="xl:col-span-4">
                    <CompactCard title="Analysis & Events" icon={<Bell className="w-4 h-4" />} color="rose" className="h-[360px]">
                        <div className="space-y-4 flex-1 overflow-y-auto pr-1 custom-scroll">
                            <div className="text-[10px] font-bold text-slate-800 mb-2 uppercase flex justify-between items-center">
                                <span>Issues Detected</span>
                                <span className="bg-slate-100 px-1.5 rounded">{data.analysis.total_critical + data.analysis.total_warnings}</span>
                            </div>
                            {data.analysis.critical_issues.length > 0 && (
                                <div className="space-y-1.5">
                                    <div className="text-[9px] font-black text-red-500 uppercase tracking-widest flex items-center gap-1">
                                        <ShieldAlert className="w-3 h-3" /> Critical
                                    </div>
                                    {data.analysis.critical_issues.map((issue, idx) => (
                                        <div key={idx} className="text-[11px] font-medium text-slate-600 leading-tight bg-red-50/80 p-2 rounded border border-red-100 flex gap-2">
                                            <div className="w-1 h-1 rounded-full bg-red-500 mt-1.5 flex-shrink-0 shadow-[0_0_4px_rgba(239,68,68,0.4)]" />
                                            {issue}
                                        </div>
                                    ))}
                                </div>
                            )}

                            {data.analysis.warnings.length > 0 && (
                                <div className="space-y-1.5">
                                    <div className="text-[9px] font-black text-amber-500 uppercase tracking-widest flex items-center gap-1">
                                        <AlertTriangle className="w-3 h-3" /> Warnings
                                    </div>
                                    {data.analysis.warnings.map((issue, idx) => (
                                        <div key={idx} className="text-[11px] font-medium text-slate-600 leading-tight bg-amber-50/80 p-2 rounded border border-amber-100 flex gap-2">
                                            <div className="w-1 h-1 rounded-full bg-amber-500 mt-1.5 flex-shrink-0" />
                                            {issue}
                                        </div>
                                    ))}
                                </div>
                            )}

                            {data.analysis.critical_issues.length === 0 && data.analysis.warnings.length === 0 && (
                                <div className="h-full flex flex-col items-center justify-center py-20 text-slate-300">
                                    <CheckCircle2 className="w-10 h-10 text-emerald-500 mb-2 opacity-20" />
                                    <p className="text-[10px] font-black uppercase tracking-widest">System Healthy</p>
                                </div>
                            )}
                        </div>
                    </CompactCard>
                </div>
            </div>
        </div>
    );
};

const NVRStatsCard: React.FC<{ label: string; value: string; subtitle: string; icon: React.ReactNode; color: string }> = ({ label, value, subtitle, icon, color }) => {
    const colorMap: any = {
        orange: 'text-[#422568] bg-[#422568] border-[#422568]',
        red: 'text-red-600 bg-red-50 border-red-100',
        amber: 'text-amber-600 bg-amber-50 border-amber-100',
        blue: 'text-blue-600 bg-blue-50 border-blue-100',
    };

    return (
        <div className="bg-white p-3 rounded-xl border border-slate-100 shadow-sm flex items-center gap-3 transition-all hover:shadow-md hover:border-slate-200 cursor-default group">
            <div className={`p-2.5 rounded-lg transition-transform group-hover:scale-110 ${colorMap[color].split(' ')[1]} ${colorMap[color].split(' ')[0]}`}>
                {icon}
            </div>
            <div className="min-w-0 flex-1">
                <p className={`text-[9px] font-black uppercase tracking-wider leading-none mb-1 opacity-70 ${colorMap[color].split(' ')[0]}`}>{label}</p>
                <p className="text-sm font-extrabold text-slate-800 leading-none truncate">{value}</p>
                <p className="text-[9px] font-bold text-slate-800 mt-1 truncate">{subtitle}</p>
            </div>
        </div>
    );
};

const DeviceDetailItem: React.FC<{ label: string; value: string; icon: React.ReactNode; iconColor: string; warning?: boolean; success?: boolean }> = ({ label, value, icon, iconColor, warning, success }) => (
    <div className="flex flex-col p-2 bg-slate-50 border border-slate-100 rounded-lg min-w-0 flex-1">
        <span className="text-[9px] font-black uppercase text-slate-800 tracking-wider flex items-center gap-1 mb-1 truncate">
            <span className={iconColor}>{icon}</span>
            {label}
        </span>
        <span className={`text-[11px] font-extrabold truncate ${warning ? 'text-amber-600 bg-amber-50/50 px-1.5 py-0.5 rounded border border-amber-100/50 w-fit font-black' :
            success ? 'text-emerald-600 bg-emerald-50/50 px-1.5 py-0.5 rounded border border-emerald-100/50 w-fit font-black' :
                'text-slate-800'
            }`} title={value}>
            {value}
        </span>
    </div>
);

const CompactCard: React.FC<{ title: string; icon: React.ReactNode; color: string; children: React.ReactNode; className?: string }> = ({ title, icon, color, children, className }) => {
    const colorMap: any = {
        indigo: 'bg-indigo-50/50 text-indigo-700 border-indigo-100',
        orange: 'bg-[#422568]/20 text-[#422568] border-[#422568]/20',
        amber: 'bg-amber-50/50 text-amber-700 border-amber-100',
        rose: 'bg-rose-50/50 text-rose-700 border-rose-100',
    };
    return (
        <div className={`bg-white rounded-xl border border-slate-100 shadow-sm flex flex-col overflow-hidden ${className || ''}`}>
            <div className={`px-3 py-2 border-b flex items-center gap-2 ${colorMap[color]}`}>
                <div className="opacity-80">{icon}</div>
                <h2 className="text-[11px] font-black uppercase tracking-wider">{title}</h2>
            </div>
            <div className="px-3 pb-3flex-1 flex flex-col overflow-hidden">{children}</div>
        </div>
    );
};

const StatusDot: React.FC<{ active: boolean; color: string }> = ({ active, color }) => (
    <div className={`w-1.5 h-1.5 rounded-full ${active ? `${color} shadow-sm` : 'bg-slate-100'}`} />
);

const InfoRow: React.FC<{ label: string; value: string; icon: React.ReactNode; iconColor: string; labelColor: string; success?: boolean; warning?: boolean; error?: boolean; highlight?: boolean }> = ({ label, value, icon, iconColor, labelColor, success, warning, error, highlight }) => (
    <div className="flex justify-between items-center py-0.5">
        <span className={`text-[10px] font-black uppercase tracking-tight flex items-center gap-1.5 ${labelColor}`}>
            <span className={`${iconColor} opacity-90`}>{icon}</span>
            {label}
        </span>
        <span className={`text-[10px] font-black px-1.5 py-0.5 rounded ${error ? 'bg-red-50 text-red-600' :
            warning ? 'bg-amber-50 text-amber-600' :
                success ? 'bg-emerald-50 text-emerald-600' :
                    highlight ? 'bg-[#422568] text-white' :
                        'bg-slate-50 text-slate-800'
            }`}>
            {value}
        </span>
    </div>
);

// const StaticRow: React.FC<{ label: string; value: string; icon: React.ReactNode; iconColor: string; labelColor: string }> = ({ label, value, icon, iconColor, labelColor }) => (
//     <div className="flex justify-between items-center py-0.5">
//         <span className={`text-[10px] font-black uppercase tracking-tight flex items-center gap-1.5 ${labelColor}`}>
//             <span className={`${iconColor} opacity-90`}>{icon}</span>
//             {label}
//         </span>
//         <span className="text-[10px] font-black text-slate-800 truncate ml-2 max-w-[60%] text-right">{value}</span>
//     </div>
// );

export default NVRDashboard;
